import React, { useRef, useEffect, KeyboardEvent } from 'react';
import { Box, IconButton, CircularProgress, Tooltip } from '@mui/material';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import MicOutlinedIcon from '@mui/icons-material/MicOutlined';

interface Props {
    value: string;
    onChange: (value: string) => void;
    onSend: () => void;
    isLoading: boolean;
    disabled?: boolean;
    placeholder?: string;
}

const ChatInput: React.FC<Props> = ({
    value,
    onChange,
    onSend,
    isLoading,
    disabled = false,
    placeholder = 'Ask about your collection...',
}) => {
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    // Auto-resize textarea as content grows
    useEffect(() => {
        const el = textareaRef.current;
        if (!el) return;
        el.style.height = 'auto';
        el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
    }, [value]);

    // Focus on mount
    useEffect(() => {
        textareaRef.current?.focus();
    }, []);

    const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            if (canSend) onSend();
        }
    };

    const canSend = value.trim().length > 0 && !isLoading && !disabled;

    return (
        <Box
            sx={{
                px: 1.25,
                py: 1,
                borderTop: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                flexDirection: 'column',
                gap: 0.75,
                flexShrink: 0,
                bgcolor: 'background.paper',
            }}
        >
            {/* Textarea + send row */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'flex-end',
                    gap: 0.75,
                    border: '1px solid',
                    borderColor: value ? 'primary.light' : 'divider',
                    borderRadius: 2,
                    px: 1.25,
                    py: 0.75,
                    bgcolor: 'background.default',
                    transition: 'border-color 0.15s',
                    '&:focus-within': {
                        borderColor: 'primary.main',
                    },
                }}
            >
                <textarea
                    ref={textareaRef}
                    value={value}
                    onChange={e => onChange(e.target.value)}
                    onKeyDown={handleKeyDown}
                    disabled={isLoading || disabled}
                    placeholder={placeholder}
                    rows={1}
                    style={{
                        flex: 1,
                        border: 'none',
                        outline: 'none',
                        resize: 'none',
                        background: 'transparent',
                        fontSize: 13,
                        lineHeight: 1.55,
                        color: 'inherit',
                        fontFamily: 'inherit',
                        padding: 0,
                        minHeight: 22,
                        maxHeight: 120,
                        overflowY: 'auto',
                    }}
                />

                {/* Send button */}
                <Tooltip title={isLoading ? 'Thinking...' : 'Send (Enter)'}>
                    <span>
                        <IconButton
                            onClick={onSend}
                            disabled={!canSend}
                            size="small"
                            sx={{
                                bgcolor: canSend ? '#534AB7' : 'action.disabledBackground',
                                color: canSend ? '#fff' : 'action.disabled',
                                borderRadius: 1.5,
                                width: 30,
                                height: 30,
                                flexShrink: 0,
                                transition: 'background-color 0.15s',
                                '&:hover': {
                                    bgcolor: canSend ? '#3C3489' : 'action.disabledBackground',
                                },
                            }}
                        >
                            {isLoading
                                ? <CircularProgress size={13} sx={{ color: 'text.disabled' }} />
                                : <ArrowUpwardIcon sx={{ fontSize: 15 }} />
                            }
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>

            {/* Hint row */}
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    px: 0.25,
                }}
            >
                <Box
                    component="span"
                    sx={{ fontSize: 10, color: 'text.disabled' }}
                >
                    {isLoading ? 'Analysing your collection...' : 'Enter to send · Shift+Enter for new line'}
                </Box>
                <Box
                    component="span"
                    sx={{
                        fontSize: 10,
                        color: value.length > 450 ? 'warning.main' : 'text.disabled',
                    }}
                >
                    {value.length > 0 ? `${value.length}/500` : ''}
                </Box>
            </Box>
        </Box>
    );
};

export default ChatInput;